import type { ElementKey } from '@/lib/experience'
import { ELEMENTS, QUESTIONS } from '@/lib/experience'
import { AnimatePresence, motion } from 'framer-motion'
import { useState } from 'react'
import ProgressBar from './ProgressBar'

interface Props {
  onComplete: (answers: Record<number, 'A' | 'B'>) => void
}

function StepQuestions({ onComplete }: Props) {
  const [index, setIndex] = useState(0)
  const [answers, setAnswers] = useState<Record<number, 'A' | 'B'>>({})
  const [locked, setLocked] = useState<null | 'A' | 'B'>(null)

  const q = QUESTIONS[index]
  const elementMeta = ELEMENTS[q.element as ElementKey]

  const select = (choice: 'A' | 'B') => {
    if (locked) return
    setLocked(choice)
    const next = { ...answers, [q.id]: choice }
    setAnswers(next)

    setTimeout(() => {
      if (index < QUESTIONS.length - 1) {
        setIndex(index + 1)
        setLocked(null)
      } else {
        onComplete(next)
      }
    }, 620)
  }

  return (
    <div className="w-full max-w-2xl mx-auto">
      <div className="mb-10">
        <ProgressBar current={index + 1} total={QUESTIONS.length} accent={elementMeta.accentSoft} />
      </div>

      {/* Instruction pill */}
      <div className="text-center mb-8">
        <p className="text-[12px] sm:text-[13px] tracking-[0.28em] uppercase font-body" style={{ color: elementMeta.accentSoft }}>
          Elemento {elementMeta.label}
        </p>
      </div>

      <p className="text-center type-label text-[var(--ink-soft)] font-body italic mb-8 max-w-lg mx-auto">
        Escolha a opção que mais se parece com o que você vive agora. Não existe resposta certa ou errada.
      </p>

      <AnimatePresence mode="wait">
        <motion.div
          key={q.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <h2 className="font-display type-section text-center text-[var(--ink)] mb-8 md:mb-10 px-2">
            {q.prompt}
          </h2>

          <div className="space-y-3 sm:space-y-4">
            {(['A', 'B'] as const).map((opt) => {
              const text = opt === 'A' ? q.optionA : q.optionB
              const isSelected = locked === opt
              const selectedClass = opt === 'A' ? elementMeta.selectedClass : 'selected-neutral'
              return (
                <button
                  key={opt}
                  onClick={() => select(opt)}
                  disabled={!!locked}
                  className={`choice-card w-full text-left p-4 sm:p-5 md:p-6 rounded-sm flex gap-3 sm:gap-4 items-start min-h-[64px] ${isSelected ? `selected ${selectedClass}` : ''}`}
                >
                  <span className="tag-letter">{opt}</span>
                  <span className="font-body type-card text-[var(--ink)] pt-1">
                    {text}
                  </span>
                </button>
              )
            })}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

export default StepQuestions
